import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScheduleSelect } from "@/components/molecules/schedule-select";
import { useUser } from "@/features/auth/hooks/use-user";
import { useVoiceNumbers } from "@/features/connectors/hooks/use-voice-numbers";
import { useDeviceBases } from "@/features/devices/hooks/use-devices";
import type { Device, DeviceInput } from "@/features/devices/types";
import { useClientId } from "@/features/utils/api-scope";
import { useTranslation } from "@/features/utils/i18n";

export type DeviceFormData = {
  label: string;
  caller_id: string;
  connector_id: string;
  /** The base this handset runs on. Empty is normal — softphones have none. */
  base_id: string;
  mac: string;
  inbound_enabled: boolean;
  /** May the agent offer this phone to a caller by name? Off by default. */
  accept_transfers: boolean;
  /** Opening hours of the place it sits in. "NONE" = whenever it is registered. */
  schedule_id: string;
  /** Platform staff only; shown to everyone so a refusal is explicable. */
  outbound_enabled: boolean;
  /** Platform staff only. Comma-separated in the form, an array on the wire. */
  max_prefixes: string;
  /** The tenant's own choice, inside `max_prefixes`. Comma-separated. */
  allowed_prefixes: string;
  monthly_minutes_cap: string;
  /** Platform staff only, in seconds. Empty = the platform default. */
  max_call_seconds: string;
};

export const emptyDeviceForm = (device?: Device): DeviceFormData => ({
  label: device?.label || "",
  caller_id: device?.caller_id || "",
  connector_id: device?.connector_id || "",
  base_id: device?.base_id || "",
  mac: device?.mac || "",
  inbound_enabled: device?.inbound_enabled ?? true,
  // Off for a new handset, and deliberately not hidden at creation like the two
  // switches below: those are capabilities that are on and need no decision,
  // this is a decision — most phones are somebody's desk, not the front desk.
  accept_transfers: device?.accept_transfers ?? false,
  schedule_id: device?.schedule_id || "NONE",
  // On for a handset that does not exist yet: a phone is installed to ring and
  // to call out, and ticking both every single time was a step that never went
  // the other way. It stays *authorised* by HeyUp — a tenant's create sends
  // this and the backend drops it (`sanitize` in services/devices/index.ts), so
  // only staff actually grant it. Turning it off afterwards is what the edit
  // dialog is for, which is why the two switches only show there.
  outbound_enabled: device?.outbound_enabled ?? true,
  max_prefixes: (device?.max_prefixes || []).join(", "),
  allowed_prefixes: (device?.allowed_prefixes || []).join(", "),
  monthly_minutes_cap: device?.monthly_minutes_cap
    ? String(device.monthly_minutes_cap)
    : "",
  max_call_seconds: device?.max_call_seconds
    ? String(device.max_call_seconds)
    : "",
});

export const toDeviceInput = (form: DeviceFormData): DeviceInput => ({
  label: form.label.trim(),
  caller_id: form.caller_id.trim(),
  connector_id: form.connector_id || undefined,
  base_id: form.base_id,
  mac: form.mac.trim(),
  inbound_enabled: form.inbound_enabled,
  accept_transfers: form.accept_transfers,
  // "NONE" is the picker's spelling of "no schedule"; the backend's is "".
  schedule_id:
    form.accept_transfers && form.schedule_id !== "NONE"
      ? form.schedule_id
      : "",
  // The three platform-staff fields are sent like any other: the backend keeps
  // them only for a SYSADMIN and silently ignores them otherwise
  // (`sanitize` in services/devices/index.ts), which is what lets this one form
  // serve both audiences without a tenant's save being rejected for echoing
  // what it was shown read-only.
  outbound_enabled: form.outbound_enabled,
  max_prefixes: splitPrefixes(form.max_prefixes),
  allowed_prefixes: splitPrefixes(form.allowed_prefixes),
  monthly_minutes_cap: Number(form.monthly_minutes_cap) || 0,
  max_call_seconds: Number(form.max_call_seconds) || 0,
});

const splitPrefixes = (raw: string): string[] =>
  raw
    .split(",")
    .map((prefix) => prefix.trim())
    .filter(Boolean);

export const DeviceForm = ({
  value,
  onChange,
  creating,
}: {
  value: DeviceFormData;
  onChange: (value: DeviceFormData) => void;
  /**
   * Creation hides the two capability switches: a new handset rings and calls
   * out, so they are on and there is nothing to decide (see
   * {@link emptyDeviceForm}). Turning one off is an edit, not a step of the
   * creation form.
   */
  creating?: boolean;
}) => {
  const { t } = useTranslation("devices");
  const { numbers, isLoading: numbersLoading } = useVoiceNumbers();
  const clientId = useClientId();
  const { data: bases = [] } = useDeviceBases(clientId || "");
  const { data: user } = useUser();
  // Outbound authorisation, the prefix ceiling and the per-call ceiling decide
  // what this handset can cost *us* — they are ours to set, and the backend
  // refuses them from anyone else. Shown read-only rather than hidden: a client
  // whose handset "just cannot call Belgium" needs to see why, and who to ask.
  const isSysadmin = user?.role === "SYSADMIN";
  const set = <K extends keyof DeviceFormData>(
    key: K,
    fieldValue: DeviceFormData[K],
  ) => onChange({ ...value, [key]: fieldValue });

  // A handset presenting a number the tenant does not own is refused by the
  // backend and, behind it, by the operator — so the field is a list of the
  // numbers we have rather than a text box that can only be typed wrong. An
  // existing handset whose number has since left the channels keeps its value
  // in the list, otherwise opening the edit dialog would silently blank it.
  const options = numbers.some((entry) => entry.number === value.caller_id)
    ? numbers
    : [
        ...numbers,
        ...(value.caller_id
          ? [
              {
                number: value.caller_id,
                connector_id: value.connector_id,
                connector_type: "twilio" as const,
                connector_label: t("devices.form.callerIdOrphan"),
              },
            ]
          : []),
      ];

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="device-label">{t("devices.form.label")}</Label>
        <Input
          id="device-label"
          value={value.label}
          placeholder={t("devices.form.labelPlaceholder")}
          onChange={(e) => set("label", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="device-caller-id">{t("devices.form.callerId")}</Label>
        <Select
          value={value.caller_id}
          onValueChange={(number) =>
            onChange({
              ...value,
              caller_id: number,
              // The handset hangs off the channel that carries its number:
              // picking the number is picking the channel, and asking for both
              // separately is asking the user to keep two fields consistent.
              connector_id:
                options.find((entry) => entry.number === number)
                  ?.connector_id || "",
            })
          }
          disabled={numbersLoading}
        >
          <SelectTrigger id="device-caller-id">
            <SelectValue
              placeholder={
                numbersLoading
                  ? t("devices.form.callerIdLoading")
                  : t("devices.form.callerIdPlaceholder")
              }
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((entry) => (
              <SelectItem key={entry.number} value={entry.number}>
                {entry.number} — {entry.connector_label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {options.length === 0 && !numbersLoading
            ? t("devices.form.callerIdEmpty")
            : t("devices.form.callerIdHelp")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="device-mac">{t("devices.form.mac")}</Label>
        <Input
          id="device-mac"
          value={value.mac}
          placeholder="00:11:22:33:44:55"
          onChange={(e) => set("mac", e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="device-base">{t("devices.form.base")}</Label>
        {/*
          Attaching a handset to a base is done here rather than from the base,
          because this is where somebody is when they know the answer: they are
          declaring a phone, and they are holding it. `NONE` stands in for the
          empty string — a Radix `SelectItem` cannot carry one as its value.
        */}
        <Select
          value={value.base_id || "none"}
          onValueChange={(base) => set("base_id", base === "none" ? "" : base)}
        >
          <SelectTrigger id="device-base">
            <SelectValue placeholder={t("devices.form.baseNone")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t("devices.form.baseNone")}</SelectItem>
            {bases.map((base) => (
              <SelectItem key={base.id} value={base.id!}>
                {base.label}
                {base.brand ? ` — ${base.brand}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          {t("devices.form.baseHelp")}
        </p>
      </div>

      {/*
        The front desk, as opposed to somebody's desk. On, the agent may offer
        this phone to a caller by name — "je vous passe l'accueil" — and the
        label above is what it says out loud, so it wants to be a name a caller
        would recognise rather than an inventory tag.
      */}
      <div className="space-y-4 rounded-md border px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>{t("devices.form.acceptTransfers")}</Label>
            <p className="text-muted-foreground text-xs">
              {t("devices.form.acceptTransfersHelp")}
            </p>
          </div>
          <Switch
            checked={value.accept_transfers}
            onCheckedChange={(checked) => set("accept_transfers", checked)}
          />
        </div>

        {value.accept_transfers && (
          <div className="space-y-2">
            <Label>{t("devices.form.schedule")}</Label>
            {/*
              Its own hours, not a member's: the reception desk is open when the
              salon is open, whoever happens to be standing at it. Nothing
              selected means the company's default hours: a desk phone stays
              registered all night, so "whenever it is registered" offered it
              to callers long after closing.
            */}
            <ScheduleSelect
              value={value.schedule_id}
              onChange={(schedule) => set("schedule_id", schedule)}
              allowNone={true}
              placeholder={t("devices.form.schedulePlaceholder")}
            />
            <p className="text-muted-foreground text-xs">
              {t("devices.form.scheduleHelp")}
            </p>
          </div>
        )}
      </div>

      {/*
        Whether HeyUp may ring this handset at all: the switch a transfer
        depends on, and the one a client looks for when a phone that is
        registered and green never rings. Hidden while creating — a phone is
        installed to ring — and shown on edit, which is the only way back.
      */}
      {!creating && (
        <>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div className="space-y-0.5">
              <Label>{t("devices.form.inbound")}</Label>
              <p className="text-muted-foreground text-xs">
                {t("devices.form.inboundHelp")}
              </p>
            </div>
            <Switch
              checked={value.inbound_enabled}
              onCheckedChange={(checked) => set("inbound_enabled", checked)}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div className="space-y-0.5">
              <Label>{t("devices.form.outbound")}</Label>
              <p className="text-muted-foreground text-xs">
                {isSysadmin
                  ? t("devices.form.outboundHelp")
                  : t("devices.form.outboundStaffOnly")}
              </p>
            </div>
            <Switch
              checked={value.outbound_enabled}
              disabled={!isSysadmin}
              onCheckedChange={(checked) => set("outbound_enabled", checked)}
            />
          </div>
        </>
      )}

      {/* The ceiling. Platform staff only — see the comment on isSysadmin. */}
      <div className="space-y-2">
        <Label htmlFor="device-max-prefixes">
          {t("devices.form.maxPrefixes")}
        </Label>
        <Input
          id="device-max-prefixes"
          value={value.max_prefixes}
          placeholder={t("devices.form.maxPrefixesPlaceholder")}
          disabled={!isSysadmin}
          onChange={(e) => set("max_prefixes", e.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {t("devices.form.maxPrefixesHelp")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="device-prefixes">{t("devices.form.prefixes")}</Label>
        <Input
          id="device-prefixes"
          value={value.allowed_prefixes}
          placeholder={value.max_prefixes || "+33, +32"}
          onChange={(e) => set("allowed_prefixes", e.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {t("devices.form.prefixesHelp")}{" "}
          {t("devices.form.prefixesWithinCeiling")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="device-cap">{t("devices.form.cap")}</Label>
        <Input
          id="device-cap"
          type="number"
          min={0}
          value={value.monthly_minutes_cap}
          placeholder="0"
          onChange={(e) => set("monthly_minutes_cap", e.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {t("devices.form.capHelp")}
        </p>
      </div>

      {/* Platform staff only, like the two above. */}
      <div className="space-y-2">
        <Label htmlFor="device-max-call">
          {t("devices.form.maxCallSeconds")}
        </Label>
        <Input
          id="device-max-call"
          type="number"
          min={0}
          value={value.max_call_seconds}
          placeholder="3600"
          disabled={!isSysadmin}
          onChange={(e) => set("max_call_seconds", e.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {t("devices.form.maxCallSecondsHelp")}
        </p>
      </div>
    </div>
  );
};
